import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Card, CardContent,
  Container, Chip, TextField, MenuItem, Alert,
  CircularProgress, Paper, Divider
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ComputerIcon from '@mui/icons-material/Computer';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SEO, { organizationSchema, breadcrumbSchema, BRAND_PHONE, BRAND_PHONE_TEL, BRAND_WHATSAPP } from '../seo/SEO';

function SectionLabel({ text, color = '#0A1628', align = 'center' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, justifyContent: align }}>
      <Box sx={{ width: 32, height: 2, backgroundColor: color, borderRadius: 1 }} />
      <Typography sx={{ color, fontWeight: 600, fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase' }}>
        {text}
      </Typography>
    </Box>
  );
}

const SLIDES = [
  {
    badge: 'Certification Consultancy',
    heading: 'Achieve Compliance.',
    subheading: 'Build Trust.',
    desc: 'ISO, BIS, FSSAI, BEE and more — complete certification support from documentation to final approval. We make compliance simple.',
    btnLabel: 'Explore Certification',
    btnPath: '/services/certification',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
    imageAlt: 'Certification Consultancy',
    accent: '#038ED3',
  },
  {
    badge: 'IT Solutions',
    heading: 'Build Smarter.',
    subheading: 'Scale Faster.',
    desc: 'Custom software, cloud infrastructure, and cybersecurity solutions built for Indian businesses ready to compete at the highest level.',
    btnLabel: 'Explore IT Solutions',
    btnPath: '/services/it-solutions',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80',
    imageAlt: 'IT Solutions',
    accent: '#0E7490',
  },
  {
    badge: 'Energy Consultancy',
    heading: 'Reduce Costs.',
    subheading: 'Go Sustainable.',
    desc: 'Energy audits, solar solutions and BEE compliance strategies that cut energy bills and ensure your business meets regulatory standards.',
    btnLabel: 'Explore Energy Services',
    btnPath: '/services/energy',
    image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=1200&q=80',
    imageAlt: 'Energy Consultancy',
    accent: '#B45309',
  },
];

function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = (index) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => { setCurrent(index); setAnimating(false); }, 350);
  };

  const prev = () => goTo(current === 0 ? SLIDES.length - 1 : current - 1);
  const next = () => goTo(current === SLIDES.length - 1 ? 0 : current + 1);

  useEffect(() => {
    const timer = setInterval(() => next(), 6000);
    return () => clearInterval(timer);
  }, [current]);

  const slide = SLIDES[current];

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', position: 'relative', overflow: 'hidden' }}>

      {/* MOBILE */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ position: 'relative', width: '100%', height: 220, overflow: 'hidden' }}>
          <Box sx={{ opacity: animating ? 0 : 1, transition: 'opacity 0.35s ease', width: '100%', height: '100%' }}>
            <img src={slide.image} alt={slide.imageAlt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4))' }} />
          </Box>
          <Box sx={{ position: 'absolute', bottom: 14, left: 14, backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 1, px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: slide.accent }} />
            <Typography sx={{ color: '#0A1628', fontWeight: 600, fontSize: 12 }}>{slide.imageAlt}</Typography>
          </Box>
        </Box>

        <Box sx={{ px: 3, pt: 3.5, pb: 4, opacity: animating ? 0 : 1, transform: animating ? 'translateY(8px)' : 'translateY(0)', transition: 'all 0.35s ease' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
            {SLIDES.map((_, i) => (
              <Box key={i} onClick={() => goTo(i)} sx={{ width: i === current ? 22 : 7, height: 3, borderRadius: 2, backgroundColor: i === current ? '#0A1628' : '#CBD5E1', cursor: 'pointer', transition: 'all 0.3s ease' }} />
            ))}
          </Box>
          <Chip label={slide.badge} sx={{ backgroundColor: '#EFF6FF', color: '#038ED3', border: '1px solid #BFDBFE', fontWeight: 600, fontSize: 11, mb: 2, borderRadius: 1, height: 26 }} />
          <Typography sx={{ fontWeight: 800, color: '#0A1628', fontSize: 30, lineHeight: 1.15, letterSpacing: '-1px', mb: 0.3 }}>{slide.heading}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 30, lineHeight: 1.15, letterSpacing: '-1px', mb: 2, color: '#94A3B8' }}>{slide.subheading}</Typography>
          <Typography sx={{ fontSize: 14, color: '#475569', mb: 3.5, lineHeight: 1.8 }}>{slide.desc}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button component={Link} to={slide.btnPath} variant="contained" endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
              sx={{ backgroundColor: '#0A1628', textTransform: 'none', fontWeight: 600, fontSize: 14, py: 1.4, borderRadius: 1, boxShadow: 'none', '&:hover': { backgroundColor: '#1E3A5F' } }}>
              {slide.btnLabel}
            </Button>
            <Button component={Link} to="/contact" variant="outlined"
              sx={{ color: '#0A1628', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 500, fontSize: 14, py: 1.4, borderRadius: 1, '&:hover': { borderColor: '#0A1628', backgroundColor: '#F8FAFC' } }}>
              Free Consultation
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3.5 }}>
            <Box onClick={prev} sx={{ width: 36, height: 36, borderRadius: 1, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748B', '&:hover': { backgroundColor: '#0A1628', color: '#fff', borderColor: '#0A1628' }, transition: 'all 0.2s' }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
            </Box>
            <Box onClick={next} sx={{ width: 36, height: 36, borderRadius: 1, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748B', '&:hover': { backgroundColor: '#0A1628', color: '#fff', borderColor: '#0A1628' }, transition: 'all 0.2s' }}>
              <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: '#94A3B8', ml: 0.5 }}>
              {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* DESKTOP */}
      <Container maxWidth="lg" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Grid container spacing={0} alignItems="stretch" sx={{ minHeight: '88vh' }}>
          <Grid item md={6} sx={{ display: 'flex', alignItems: 'center', py: 6, pr: 6 }}>
            <Box sx={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(12px)' : 'translateY(0)', transition: 'all 0.35s ease', width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
                {SLIDES.map((_, i) => (
                  <Box key={i} onClick={() => goTo(i)} sx={{ width: i === current ? 24 : 8, height: 3, borderRadius: 2, backgroundColor: i === current ? '#0A1628' : '#CBD5E1', cursor: 'pointer', transition: 'all 0.3s ease' }} />
                ))}
              </Box>
              <Chip label={slide.badge} sx={{ backgroundColor: '#EFF6FF', color: '#038ED3', border: '1px solid #BFDBFE', fontWeight: 600, fontSize: 12, mb: 3, borderRadius: 1, letterSpacing: 0.3 }} />
              <Typography variant="h1" sx={{ fontWeight: 800, color: '#0A1628', fontSize: 60, lineHeight: 1.1, letterSpacing: '-2px', mb: 0.5 }}>{slide.heading}</Typography>
              <Typography variant="h2" sx={{ fontWeight: 800, fontSize: 60, lineHeight: 1.1, letterSpacing: '-2px', mb: 3, color: '#94A3B8' }}>{slide.subheading}</Typography>
              <Typography sx={{ fontSize: 17, color: '#475569', mb: 5, lineHeight: 1.8, maxWidth: 460 }}>{slide.desc}</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button component={Link} to={slide.btnPath} variant="contained" endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{ backgroundColor: '#0A1628', textTransform: 'none', fontWeight: 600, fontSize: 14, px: 3.5, py: 1.4, borderRadius: 1, boxShadow: 'none', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(10,22,40,0.2)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
                  {slide.btnLabel}
                </Button>
                <Button component={Link} to="/contact" variant="outlined"
                  sx={{ color: '#0A1628', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 500, fontSize: 14, px: 3.5, py: 1.4, borderRadius: 1, '&:hover': { borderColor: '#0A1628', backgroundColor: '#F8FAFC' }, transition: 'all 0.2s' }}>
                  Free Consultation
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 5, alignItems: 'center' }}>
                <Box onClick={prev} sx={{ width: 40, height: 40, borderRadius: 1, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748B', '&:hover': { backgroundColor: '#0A1628', color: '#fff', borderColor: '#0A1628' }, transition: 'all 0.2s' }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
                </Box>
                <Box onClick={next} sx={{ width: 40, height: 40, borderRadius: 1, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748B', '&:hover': { backgroundColor: '#0A1628', color: '#fff', borderColor: '#0A1628' }, transition: 'all 0.2s' }}>
                  <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
                </Box>
                <Typography sx={{ fontSize: 13, color: '#94A3B8', ml: 1 }}>
                  {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item md={6} sx={{ display: 'flex', alignItems: 'center', py: 5 }}>
            <Box sx={{ width: '100%', opacity: animating ? 0 : 1, transform: animating ? 'scale(0.98)' : 'scale(1)', transition: 'all 0.35s ease', position: 'relative' }}>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', aspectRatio: '4/3', boxShadow: '0 24px 60px rgba(0,0,0,0.12)', border: '1px solid #E2E8F0' }}>
                <img src={slide.image} alt={slide.imageAlt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
              <Box sx={{ position: 'absolute', bottom: 20, left: 20, backgroundColor: '#fff', borderRadius: 1, px: 2, py: 1, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: slide.accent }} />
                <Typography sx={{ color: '#0A1628', fontWeight: 600, fontSize: 13 }}>{slide.imageAlt}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

const SERVICES = [
  { icon: <VerifiedIcon sx={{ fontSize: 28, color: '#038ED3' }} />, title: 'Certification Consultancy', description: 'ISO, BIS, BEE, FSSAI and more. End-to-end support from documentation to final certificate.', path: '/services/certification', bg: '#EFF6FF', accent: '#038ED3', tags: ['ISO 9001', 'BIS', 'FSSAI', 'BEE'] },
  { icon: <ComputerIcon sx={{ fontSize: 28, color: '#0E7490' }} />, title: 'IT Solutions', description: 'Custom software, cloud infrastructure, and cybersecurity tailored to your business.', path: '/services/it-solutions', bg: '#ECFEFF', accent: '#0E7490', tags: ['Cloud', 'Software Dev', 'Cybersecurity'] },
  { icon: <BoltIcon sx={{ fontSize: 28, color: '#B45309' }} />, title: 'Energy Consultancy', description: 'Energy audits, solar solutions, and efficiency strategies that cut costs and ensure compliance.', path: '/services/energy', bg: '#FFFBEB', accent: '#B45309', tags: ['Energy Audit', 'Solar', 'BEE Compliance'] },
];

const WHY_US = [
  { title: 'End-to-End Support', desc: 'From first consultation to final delivery — we handle everything.' },
  { title: 'Dedicated Manager', desc: 'A single point of contact who knows your business inside out.' },
  { title: 'Transparent Process', desc: 'Real-time updates at every stage. No surprises, ever.' },
  { title: 'Pan-India Presence', desc: 'Local expertise across all major Indian cities and states.' },
  { title: 'No Hidden Charges', desc: 'Clear, upfront pricing with full cost visibility from day one.' },
  { title: 'Guaranteed Timelines', desc: 'We commit to deadlines and deliver on time, every time.' },
];

/* ─────────── HOME ─────────── */
export function Home() {
  return (
    <Box>
      <SEO
        title="VforU Global | Certification, IT Solutions & Energy Consultancy"
        description="VforU Global — Your trusted partner for Certification Consultancy (ISO, BIS, FSSAI, BEE), IT Solutions, and Energy Consultancy in India. Chennai-based, Pan-India service."
        path="/"
        structuredData={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }])]}
      />
      <HeroCarousel />

      {/* What We Specialise In */}
      <Box sx={{ backgroundColor: '#fff', py: 7, borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel text="What We Do" />
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#0A1628', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>
              Three Services. One Partner.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { icon: <VerifiedIcon sx={{ fontSize: 32, color: '#038ED3' }} />, label: 'Certification Consultancy', desc: 'ISO, BIS, FSSAI, BEE and more — complete certification support for your business compliance needs.', color: '#038ED3', bg: '#EFF6FF', border: '#BFDBFE', path: '/services/certification', tags: ['ISO 9001', 'BIS', 'FSSAI', 'BEE'] },
              { icon: <ComputerIcon sx={{ fontSize: 32, color: '#0E7490' }} />, label: 'IT Solutions', desc: 'Custom software, cloud infrastructure, cybersecurity and digital transformation for every scale.', color: '#0E7490', bg: '#ECFEFF', border: '#A5F3FC', path: '/services/it-solutions', tags: ['Cloud', 'Software', 'Cybersecurity'] },
              { icon: <BoltIcon sx={{ fontSize: 32, color: '#B45309' }} />, label: 'Energy Consultancy', desc: 'Energy audits, solar solutions and BEE compliance that cut costs and ensure sustainability.', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', path: '/services/energy', tags: ['Energy Audit', 'Solar', 'BEE'] },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Box component={Link} to={item.path} className={`slide-up delay-${i + 1}`}
                  sx={{ display: 'block', textDecoration: 'none', p: 4, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: '#fff', transition: 'all 0.25s ease', '&:hover': { borderColor: item.color, boxShadow: `0 8px 32px ${item.color}15`, transform: 'translateY(-4px)' } }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: 1.5, backgroundColor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>{item.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0A1628', mb: 1.5, letterSpacing: '-0.3px' }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.8, mb: 3 }}>{item.desc}</Typography>
                  <Divider sx={{ mb: 2.5 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
                    {item.tags.map((tag) => (
                      <Typography key={tag} sx={{ fontSize: 11, color: item.color, fontWeight: 600, backgroundColor: item.bg, px: 1.2, py: 0.4, borderRadius: 0.5 }}>{tag}</Typography>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: item.color }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>View Service</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Core Services */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: { xs: 7, md: 10 }, borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5} className="slide-left delay-1">
              <SectionLabel text="Our Services" align="left" />
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#0A1628', fontSize: { xs: 28, md: 38 }, letterSpacing: '-0.5px', mb: 2 }}>
                Built for Indian Businesses
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: 16, lineHeight: 1.9, mb: 4 }}>
                We understand the regulatory landscape, technology challenges, and energy demands that Indian businesses face every day.
              </Typography>
              <Button component={Link} to="/contact" variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
                sx={{ backgroundColor: '#0A1628', textTransform: 'none', fontWeight: 600, fontSize: 14, px: 3.5, py: 1.4, borderRadius: 1, boxShadow: 'none', '&:hover': { backgroundColor: '#1E3A5F' } }}>
                Talk to an Expert
              </Button>
            </Grid>
            <Grid item xs={12} md={7} className="slide-right delay-2">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SERVICES.map((svc) => (
                  <Paper key={svc.title} component={Link} to={svc.path} elevation={0} sx={{
                    display: 'flex', alignItems: 'center', gap: 3, p: 3,
                    borderRadius: 2, border: '1px solid #E2E8F0',
                    textDecoration: 'none', backgroundColor: '#fff',
                    transition: 'all 0.25s ease',
                    '&:hover': { borderColor: svc.accent, boxShadow: `0 4px 20px ${svc.accent}15`, transform: 'translateX(4px)' },
                  }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 1.5, backgroundColor: svc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{svc.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0A1628', mb: 0.5 }}>{svc.title}</Typography>
                      <Typography sx={{ fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>{svc.description}</Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ fontSize: 16, color: '#CBD5E1', flexShrink: 0 }} />
                  </Paper>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Us */}
      <Box sx={{ backgroundColor: '#0A1628', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <SectionLabel text="Why Choose Us" color="#475569" />
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 28, md: 38 }, letterSpacing: '-0.5px' }}>
              The VforU Global Difference
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {WHY_US.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Box className={`slide-up delay-${i + 1}`} sx={{ p: 3.5, borderRadius: 2, border: '1px solid #1E293B', transition: 'all 0.25s', '&:hover': { borderColor: '#334155', backgroundColor: '#0F2040' } }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#038ED3' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#fff', mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748B', lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 7, md: 10 }, borderBottom: '1px solid #E2E8F0' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <SectionLabel text="Get Started" />
          <Typography variant="h2" sx={{ fontWeight: 800, color: '#0A1628', fontSize: { xs: 28, md: 44 }, letterSpacing: '-1px', mb: 2 }}>
            Ready to grow your business?
          </Typography>
          <Typography sx={{ color: '#64748B', fontSize: 17, lineHeight: 1.8, mb: 5, maxWidth: 520, mx: 'auto' }}>
            Talk to our experts today. Free 30-minute consultation — no commitment required.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={Link} to="/contact" variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              sx={{ backgroundColor: '#0A1628', textTransform: 'none', fontWeight: 600, fontSize: 15, px: 5, py: 1.6, borderRadius: 1, boxShadow: 'none', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(10,22,40,0.2)' } }}>
              Book Free Consultation
            </Button>
            <Button component="a" href={BRAND_PHONE_TEL} variant="outlined" size="large"
              sx={{ color: '#0A1628', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 500, fontSize: 15, px: 5, py: 1.6, borderRadius: 1, '&:hover': { borderColor: '#0A1628', backgroundColor: '#F8FAFC' } }}>
              Call Us Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

/* ─────────── ABOUT ─────────── */
const VALUES = [
  { title: 'Integrity', desc: 'Transparent process, honest advice, no hidden charges ever.', color: '#038ED3' },
  { title: 'Excellence', desc: 'We deliver quality outcomes every single time, no exceptions.', color: '#0E7490' },
  { title: 'Partnership', desc: 'Your success is our success. We grow together as one team.', color: '#059669' },
  { title: 'Innovation', desc: 'We bring modern solutions to traditional business challenges.', color: '#B45309' },
];

const TEAM = [
  { name: 'Founder & CEO', initials: 'VG', color: '#0A1628', desc: '15+ years in certification and regulatory compliance across India.' },
  { name: 'Head of IT', initials: 'IT', color: '#0E7490', desc: 'Cloud architect and software delivery lead with 12+ years experience.' },
  { name: 'Energy Lead', initials: 'EL', color: '#B45309', desc: 'Certified energy auditor, BEE specialist with 200+ audits completed.' },
];

export function About() {
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title="About Us | VforU Global — Certification, IT & Energy Consultancy"
        description="Learn about VforU Global — Chennai-based consultancy helping Indian businesses with Certification (ISO, BIS, FSSAI, BEE), IT Solutions, and Energy Consultancy since our founding."
        path="/about"
        structuredData={[
          organizationSchema(),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
        ]}
      />
      <Box sx={{ backgroundColor: '#0A1628', py: { xs: 8, md: 11 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400&q=80)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.05 }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <SectionLabel text="About Us" color="#475569" />
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 30, md: 50 }, letterSpacing: '-1px', mb: 2.5, lineHeight: 1.15 }}>
                India's Trusted Consultancy Partner
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: 17, lineHeight: 1.9, maxWidth: 540 }}>
                VforU Global is a full-service consultancy firm helping Indian businesses navigate compliance, technology, and sustainability challenges with expertise and integrity.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={2}>
                {[
                  { value: 'Certification', desc: '20+ standards covered', color: '#038ED3' },
                  { value: 'IT Solutions', desc: 'End-to-end technology', color: '#0E7490' },
                  { value: 'Energy', desc: 'BEE & solar expertise', color: '#B45309' },
                  { value: 'Pan-India', desc: 'Across all major states', color: '#059669' },
                ].map((item) => (
                  <Grid item xs={6} key={item.value}>
                    <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #1E293B', backgroundColor: '#0F2040' }}>
                      <Box sx={{ width: 4, height: 20, backgroundColor: item.color, borderRadius: 2, mb: 1.5 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#fff', mb: 0.3 }}>{item.value}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748B' }}>{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 9 }}>
        <Grid container spacing={8} alignItems="center" sx={{ mb: 10 }}>
          <Grid item xs={12} md={6} className="slide-left delay-1">
            <SectionLabel text="Our Mission" align="left" />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0A1628', mb: 2.5, fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>
              Simplifying complexity for Indian businesses
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 16, lineHeight: 1.9, mb: 3 }}>
              Founded with a vision to make expert consultancy accessible to every Indian business — from startups to large enterprises — VforU Global brings together regulatory expertise, technology capability, and energy knowledge under one roof.
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 16, lineHeight: 1.9 }}>
              We believe every business deserves a trusted advisor who understands their challenges, speaks their language, and delivers results that actually matter.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} className="slide-right delay-2">
            <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.1)', border: '1px solid #E2E8F0' }}>
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" alt="Our Team" loading="lazy" style={{ width: '100%', height: 320, objectFit: 'cover', display: 'block' }} />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel text="Our Values" />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0A1628', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>What We Stand For</Typography>
          </Box>
          <Grid container spacing={3}>
            {VALUES.map((v, i) => (
              <Grid item xs={12} sm={6} md={3} key={v.title}>
                <Box className={`slide-up delay-${i + 1}`} sx={{ p: 3.5, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: '#fff', height: '100%', transition: 'all 0.25s', '&:hover': { borderColor: v.color, boxShadow: `0 8px 24px ${v.color}15`, transform: 'translateY(-4px)' } }}>
                  <Box sx={{ width: 4, height: 32, backgroundColor: v.color, borderRadius: 2, mb: 2.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0A1628', mb: 1 }}>{v.title}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{v.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel text="Leadership" />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0A1628', fontSize: { xs: 26, md: 36 }, letterSpacing: '-0.5px' }}>The Team Behind VforU Global</Typography>
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {TEAM.map((member, i) => (
              <Grid item xs={12} sm={6} md={4} key={member.name}>
                <Paper elevation={0} className={`slide-up delay-${i + 1}`} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', p: 4, textAlign: 'center', backgroundColor: '#fff', transition: 'all 0.25s', '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.08)', transform: 'translateY(-4px)' } }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: member.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22, mx: 'auto', mb: 2.5 }}>
                    {member.initials}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0A1628', mb: 0.5 }}>{member.name}</Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748B', lineHeight: 1.7 }}>{member.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ p: 6, borderRadius: 2, backgroundColor: '#0A1628', textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1.5, letterSpacing: '-0.5px' }}>Ready to work with us?</Typography>
          <Typography sx={{ color: '#64748B', mb: 4, fontSize: 16 }}>Let's start with a conversation about your business goals.</Typography>
          <Button component={Link} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />}
            sx={{ backgroundColor: '#fff', color: '#0A1628', textTransform: 'none', fontWeight: 700, fontSize: 14, px: 5, py: 1.5, borderRadius: 1, boxShadow: 'none', '&:hover': { backgroundColor: '#F1F5F9' } }}>
            Get In Touch
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

/* ─────────── CONTACT ─────────── */
const CONTACT_SERVICES = ['Certification Consultancy', 'IT Solutions', 'Energy Consultancy', 'General Enquiry'];

export function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const API_URL = 'https://juonabwweb.execute-api.ap-south-1.amazonaws.com/dev/contact/send';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title="Contact Us | VforU Global — Certification, IT & Energy Consultancy"
        description="Get in touch with VforU Global for Certification (ISO, BIS, FSSAI, BEE), IT Solutions, or Energy Consultancy. Chennai-based, serving businesses across India."
        path="/contact"
        structuredData={[
          organizationSchema(),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]),
        ]}
      />
      <Box sx={{ backgroundColor: '#0A1628', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionLabel text="Contact Us" color="#475569" />
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: 30, md: 48 }, letterSpacing: '-1px', mb: 2 }}>
                Let's Start a Conversation
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: 16, lineHeight: 1.9 }}>
                Whether you need certification support, an IT solution, or an energy audit — our team is ready to help. We respond within 24 hours.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {[
                  { icon: <PhoneIcon sx={{ fontSize: 20, color: '#038ED3' }} />, label: 'Phone', value: BRAND_PHONE, bg: '#0F2040' },
                  { icon: <EmailIcon sx={{ fontSize: 20, color: '#34D399' }} />, label: 'Email', value: 'info@vforuglobal.com', bg: '#0F2040' },
                  { icon: <LocationOnIcon sx={{ fontSize: 20, color: '#FCD34D' }} />, label: 'Office', value: 'Chennai, Tamil Nadu', bg: '#0F2040' },
                  { icon: <WhatsAppIcon sx={{ fontSize: 20, color: '#4ADE80' }} />, label: 'WhatsApp', value: BRAND_PHONE, bg: '#0F2040' },
                ].map((item) => (
                  <Grid item xs={6} key={item.label}>
                    <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: item.bg, border: '1px solid #1E293B' }}>
                      <Box sx={{ mb: 1.5 }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 0.3 }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: 13, color: '#E2E8F0', fontWeight: 500 }}>{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 9 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={4} className="slide-left delay-1">
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#0A1628', mb: 3 }}>How We Can Help</Typography>
            {[
              { label: 'Certification Consultancy', desc: 'ISO, BIS, FSSAI, BEE and more', color: '#038ED3' },
              { label: 'IT Solutions', desc: 'Software, cloud, cybersecurity', color: '#0E7490' },
              { label: 'Energy Consultancy', desc: 'Audits, solar, BEE compliance', color: '#B45309' },
            ].map((item) => (
              <Box key={item.label} sx={{ display: 'flex', gap: 2, mb: 3, p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: '#fff', transition: 'all 0.2s', '&:hover': { borderColor: item.color, transform: 'translateX(4px)' } }}>
                <Box sx={{ width: 4, backgroundColor: item.color, borderRadius: 2, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#0A1628', mb: 0.3 }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748B' }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
            <Box sx={{ mt: 4, p: 3, borderRadius: 2, backgroundColor: '#0A1628' }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2, fontSize: 15 }}>Business Hours</Typography>
              <Typography sx={{ color: '#64748B', fontSize: 14, mb: 0.5 }}>Monday – Saturday</Typography>
              <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 600, mb: 1.5 }}>9:00 AM – 6:00 PM</Typography>
              <Typography sx={{ color: '#64748B', fontSize: 13 }}>Sunday: Closed</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} className="slide-right delay-2">
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden', backgroundColor: '#fff' }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#0A1628' }}>Send Us a Message</Typography>
                <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.5 }}>We'll get back to you within 24 hours.</Typography>
              </Box>
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                {error && <Alert severity="error" sx={{ borderRadius: 1.5, mb: 2 }}>{error}</Alert>}
                {submitted ? (
                  <Alert severity="success" sx={{ borderRadius: 1.5, fontSize: 15 }}>
                    Thank you! We've received your message and will contact you within 24 hours.
                  </Alert>
                ) : (
                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      {[
                        { label: 'Full Name', name: 'name', type: 'text', required: true, xs: 12, sm: 6 },
                        { label: 'Email Address', name: 'email', type: 'email', required: true, xs: 12, sm: 6 },
                        { label: 'Phone Number', name: 'phone', type: 'text', required: false, xs: 12, sm: 6 },
                      ].map((field) => (
                        <Grid item xs={field.xs} sm={field.sm} key={field.name}>
                          <TextField fullWidth label={field.label} name={field.name} type={field.type}
                            required={field.required} value={form[field.name]} onChange={handleChange}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: '#FAFAFA', '&:hover fieldset': { borderColor: '#0A1628' }, '&.Mui-focused fieldset': { borderColor: '#0A1628' } } }} />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth select label="Service Required" name="service" required
                          value={form.service} onChange={handleChange}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: '#FAFAFA', '&:hover fieldset': { borderColor: '#0A1628' }, '&.Mui-focused fieldset': { borderColor: '#0A1628' } } }}>
                          {CONTACT_SERVICES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={4} label="Your Message" name="message"
                          value={form.message} onChange={handleChange}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: '#FAFAFA', '&:hover fieldset': { borderColor: '#0A1628' }, '&.Mui-focused fieldset': { borderColor: '#0A1628' } } }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}
                          endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                          sx={{ backgroundColor: '#0A1628', textTransform: 'none', fontWeight: 600, fontSize: 15, py: 1.6, borderRadius: 1.5, boxShadow: 'none', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: '0 4px 16px rgba(10,22,40,0.2)' } }}>
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}